// FLOW Platform — On-screen QWERTY keyboard.
//
// Used by the game module on mobile (and as a redundant input on desktop).
// Physical keyboard input is handled by the game module itself.
//
// The keyboard's letter keys are real <button> elements so they're
// reachable by Tab and announce their letter via aria-label. The
// physical-keyboard path bypasses focus to stay snappy.
//
// `setDisabled(true)` flips all keys to non-interactive for end-of-puzzle.
// `refresh(maxCap)` updates the visual state when the typed buffer cap
// changes (e.g. after length-hint is used) — currently a no-op since
// the cap is enforced in the game module; reserved for future
// affordances like "remaining letters" highlighting.

const ROWS = ['QWERTYUIOP', 'ASDFGHJKL', 'ZXCVBNM'];

// Where the Enter key sits. Default ON = Enter bottom-right, Backspace
// bottom-left. Players can flip this in Settings; we read it live so the
// keyboard updates without a reload.
const ENTER_RIGHT_KEY = 'flow_fht_enter_right';
function enterOnRight() {
  try {
    const v = window.localStorage.getItem(ENTER_RIGHT_KEY);
    return v === null ? true : v === '1';
  } catch (e) {
    return true;
  }
}

export function buildKeyboard({ onLetter, onBackspace, onSubmit }) {
  const wrap = document.createElement('div');
  wrap.className = 'flow-fht-keyboard';
  wrap.setAttribute('role', 'group');
  wrap.setAttribute('aria-label', 'On-screen keyboard');

  const keyButtons = [];
  const keyByLetter = {};

  // Build the two action keys once; placeActionKeys() positions them on
  // either side of the bottom row based on the Enter-position setting.
  const enterKey = makeWideKey('Enter', 'Submit word', () => onSubmit());
  const backKey = makeWideKey('\u232B', 'Backspace', () => onBackspace());
  let bottomRow = null;

  function placeActionKeys() {
    if (!bottomRow) return;
    if (enterKey.parentNode) enterKey.parentNode.removeChild(enterKey);
    if (backKey.parentNode) backKey.parentNode.removeChild(backKey);
    if (enterOnRight()) {
      // Default: Backspace on the left, Enter on the right.
      bottomRow.insertBefore(backKey, bottomRow.firstChild);
      bottomRow.appendChild(enterKey);
    } else {
      // Flipped: Enter on the left, Backspace on the right.
      bottomRow.insertBefore(enterKey, bottomRow.firstChild);
      bottomRow.appendChild(backKey);
    }
  }

  for (let r = 0; r < ROWS.length; r++) {
    const row = document.createElement('div');
    row.className = 'flow-fht-keyboard-row';
    const letters = ROWS[r];
    for (let i = 0; i < letters.length; i++) {
      const letter = letters[i];
      const btn = makeLetterKey(letter, () => onLetter(letter));
      row.appendChild(btn);
      keyButtons.push(btn);
      keyByLetter[letter.toLowerCase()] = btn;
    }
    if (r === 2) {
      bottomRow = row;
    }
    wrap.appendChild(row);
  }
  placeActionKeys();

  // The Settings sheet dispatches this after writing the preference so a
  // live keyboard repositions immediately.
  function onLayoutChange() {
    if (!wrap.isConnected) return;
    placeActionKeys();
  }
  window.addEventListener('flow-fht-kbd-layout', onLayoutChange);

  return {
    element: wrap,
    setDisabled(disabled) {
      const all = wrap.querySelectorAll('button');
      all.forEach((b) => {
        b.disabled = !!disabled;
      });
    },
    refresh(/* maxCap */) {
      // Reserved for future visual feedback. No-op for now.
    },
    // Apply per-letter color state (Wordle-style feedback). `map` is a
    // lowercase-letter -> 'green' | 'yellow' | 'absent' object. Letters
    // not present in the map are reset to neutral. Called on every render
    // by the game module for the active slot.
    setKeyColors(map) {
      map = map || {};
      const KINDS = ['green', 'yellow', 'absent'];
      Object.keys(keyByLetter).forEach((letter) => {
        const btn = keyByLetter[letter];
        KINDS.forEach((k) => btn.classList.remove('flow-fht-key-' + k));
        const kind = map[letter];
        if (kind) btn.classList.add('flow-fht-key-' + kind);
      });
    },
    teardown() {
      window.removeEventListener('flow-fht-kbd-layout', onLayoutChange);
    },
  };
}

function makeLetterKey(letter, onClick) {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'flow-fht-key flow-fht-key-letter';
  btn.textContent = letter;
  btn.setAttribute('aria-label', 'Letter ' + letter);
  // Prevent the click from stealing focus from the game container so
  // physical-keyboard input keeps working seamlessly after a tap.
  btn.addEventListener('mousedown', (e) => e.preventDefault());
  btn.addEventListener('touchstart', (e) => {
    // touchstart can't preventDefault all browsers cleanly — only block
    // the synthetic mouse events that follow.
    e.stopPropagation();
  }, { passive: true });
  btn.addEventListener('click', () => onClick());
  return btn;
}

function makeWideKey(label, srLabel, onClick) {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'flow-fht-key flow-fht-key-wide';
  btn.textContent = label;
  btn.setAttribute('aria-label', srLabel);
  btn.addEventListener('mousedown', (e) => e.preventDefault());
  btn.addEventListener('click', () => onClick());
  return btn;
}
